import { useEffect, useRef } from "react";

import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { type DayButton, DayPicker, type Locale, getDefaultClassNames } from "react-day-picker";

import { cn } from "@lib/utils";

/** A calendar component that allows users to select a date or a range of dates.
 * @prop className - Class name for the calendar container.
 * @prop showOutsideDays - Whether to show the outside days.
 * @prop captionLayout - The layout of the caption.
 * @prop locale - The locale of the calendar.
 * @prop formatters - The formatters for the calendar.
 * @prop components - The components for the calendar.
 */
const Calendar = ({
	className,
	classNames,
	showOutsideDays = true,
	captionLayout = "label",
	locale,
	formatters,
	components,
	...props
}: React.ComponentProps<typeof DayPicker> & {}) => {
	const defaultClassNames = getDefaultClassNames();

	return (
		<DayPicker
			showOutsideDays={showOutsideDays}
			className={cn(
				`bg-background group/calendar p-2 text-white [--cell-radius:var(--radius-md)] [--cell-size:--spacing(7)]
				in-data-[slot=card-content]:bg-transparent in-data-[slot=popover-content]:bg-transparent`,
				String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
				String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
				className,
			)}
			captionLayout={captionLayout}
			locale={locale}
			formatters={{
				formatMonthDropdown: (date) => date.toLocaleString(locale?.code, { month: "short" }),
				...formatters,
			}}
			classNames={{
				root: cn("w-fit", defaultClassNames.root),
				months: cn("relative flex flex-row gap-4", defaultClassNames.months),
				month: cn("flex w-full flex-col gap-4", defaultClassNames.month),
				nav: cn("absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1", defaultClassNames.nav),
				button_previous: cn(
					"size-(--cell-size) p-0 select-none aria-disabled:opacity-50",
					defaultClassNames.button_previous,
				),
				button_next: cn("size-(--cell-size) p-0 select-none aria-disabled:opacity-50", defaultClassNames.button_next),
				month_caption: cn(
					"flex h-(--cell-size) w-full items-center justify-center px-(--cell-size)",
					defaultClassNames.month_caption,
				),
				dropdowns: cn(
					"flex h-(--cell-size) w-full items-center justify-center gap-1.5 text-sm font-medium",
					defaultClassNames.dropdowns,
				),
				dropdown_root: cn(
					"cn-calendar-dropdown-root relative rounded-(--cell-radius)",
					defaultClassNames.dropdown_root,
				),
				dropdown: cn("bg-popover absolute inset-0 opacity-0", defaultClassNames.dropdown),
				caption_label: cn(
					"font-medium select-none",
					captionLayout === "label"
						? "text-sm"
						: `cn-calendar-caption-label [&>svg]:text-muted-foreground flex items-center gap-1 rounded-(--cell-radius)
							text-sm [&>svg]:size-3.5`,
					defaultClassNames.caption_label,
				),
				table: "w-full border-collapse",
				weekdays: cn("flex", defaultClassNames.weekdays),
				weekday: cn(
					"text-muted-foreground flex-1 rounded-(--cell-radius) text-[0.8rem] font-normal select-none",
					defaultClassNames.weekday,
				),
				week: cn("mt-2 flex w-full", defaultClassNames.week),
				week_number_header: cn("w-(--cell-size) select-none", defaultClassNames.week_number_header),
				week_number: cn("text-muted-foreground text-[0.8rem] select-none", defaultClassNames.week_number),
				day: cn(
					`group/day relative aspect-square h-full w-full rounded-(--cell-radius) p-0 text-center select-none
					[&:last-child[data-selected=true]_button]:rounded-e-(--cell-radius)`,
					props.showWeekNumber
						? "[&:nth-child(2)[data-selected=true]_button]:rounded-s-(--cell-radius)"
						: "[&:first-child[data-selected=true]_button]:rounded-s-(--cell-radius)",
					defaultClassNames.day,
				),
				range_start: cn(
					"relative isolate z-base rounded-s-(--cell-radius) bg-primary/90",
					defaultClassNames.range_start,
				),
				range_middle: cn("rounded-none bg-primary/55 text-white", defaultClassNames.range_middle),
				range_end: cn("relative isolate z-base rounded-e-(--cell-radius) bg-primary/90", defaultClassNames.range_end),
				today: cn(
					"bg-muted text-foreground rounded-(--cell-radius) data-[selected=true]:rounded-none",
					defaultClassNames.today,
				),
				outside: cn("text-muted-foreground aria-selected:text-muted-foreground", defaultClassNames.outside),
				disabled: cn("text-muted-foreground opacity-50", defaultClassNames.disabled),
				hidden: cn("invisible", defaultClassNames.hidden),
				...classNames,
			}}
			components={{
				Root: ({ className, rootRef, ...props }) => (
					<div data-slot="calendar" ref={rootRef} className={cn(className)} {...props} />
				),
				Chevron: ({ className, orientation, ...props }) => {
					if (orientation === "left") {
						return <ChevronLeftIcon className={cn("cn-rtl-flip size-4", className)} {...props} />;
					}

					if (orientation === "right") {
						return <ChevronRightIcon className={cn("cn-rtl-flip size-4", className)} {...props} />;
					}

					return <ChevronDownIcon className={cn("size-4", className)} {...props} />;
				},
				DayButton: ({ ...props }) => <CalendarDayButton locale={locale} {...props} />,
				WeekNumber: ({ children, ...props }) => (
					<td {...props}>
						<div className="flex size-(--cell-size) items-center justify-center text-center">{children}</div>
					</td>
				),
				...components,
			}}
			{...props}
		/>
	);
};

const CalendarDayButton = ({
	className,
	day,
	modifiers,
	locale,
	...props
}: React.ComponentProps<typeof DayButton> & { locale?: Partial<Locale> }) => {
	const defaultClassNames = getDefaultClassNames();

	const ref = useRef<HTMLButtonElement>(null);
	useEffect(() => {
		if (modifiers.focused) ref.current?.focus();
	}, [modifiers.focused]);

	return (
		<button
			data-day={day.date.toLocaleDateString(locale?.code)}
			data-selected-single={modifiers.selected && !modifiers.range_start && !modifiers.range_end ? true : undefined}
			data-range-start={modifiers.range_start || undefined}
			data-range-end={modifiers.range_end || undefined}
			data-range-middle={modifiers.range_middle || undefined}
			className={cn(
				`relative isolate z-docked flex aspect-square size-auto h-full min-h-(--cell-size) w-full min-w-(--cell-size)
				items-center justify-center gap-1 rounded-(--cell-radius) border-0 font-normal transition-colors
				hover:bg-primary hover:text-white data-[range-end=true]:rounded-s-none
				data-[range-end=true]:rounded-e-(--cell-radius) data-[range-end=true]:bg-primary
				data-[range-end=true]:text-white data-[range-middle=true]:rounded-none data-[range-middle=true]:bg-primary/55
				data-[range-middle=true]:text-white data-[range-start=true]:rounded-s-(--cell-radius)
				data-[range-start=true]:rounded-e-none data-[range-start=true]:bg-primary data-[range-start=true]:text-white
				data-[selected-single=true]:bg-primary data-[selected-single=true]:text-white [&>span]:text-xs
				[&>span]:opacity-70`,
				defaultClassNames.day_button,
				className,
			)}
			{...props}
		/>
	);
};

Calendar.displayName = "Calendar";
CalendarDayButton.displayName = "CalendarDayButton";

export { Calendar, CalendarDayButton };
